import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { useLeadEmails, LeadEmail } from "@/hooks/useLeadEmails";
import { useEmailAttachments } from "@/hooks/useEmailAttachments";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ComposeEmailDialog } from "@/components/ComposeEmailDialog";
import { EmailAttachmentItem } from "@/components/EmailAttachmentItem";
import { 
  Mail, 
  ChevronDown, 
  ChevronRight, 
  Paperclip, 
  Reply, 
  Check, 
  Circle,
  Send,
  Loader2,
  Plus
} from "lucide-react";
import { format } from "date-fns";

interface LeadEmailThreadProps {
  leadId: string;
  inboundEmailId?: string;
  clientEmail?: string;
}

interface SentEmail {
  id: string;
  lead_id: string;
  to_addresses: string[];
  cc_addresses: string[] | null;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  sent_at: string;
  sent_by: string | null;
  status: string;
  reply_to_email_id: string | null;
}

type CombinedEmail = 
  | (LeadEmail & { direction: "inbound" })
  | (SentEmail & { direction: "outbound" });

export const LeadEmailThread = ({ leadId, inboundEmailId, clientEmail }: LeadEmailThreadProps) => {
  const queryClient = useQueryClient();
  const { emails: receivedEmails, isLoading: loadingReceived, unreadCount, markAsRead, markAsReplied } = useLeadEmails(leadId);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<{
    emailId?: string;
    to: string;
    subject: string;
    originalBody?: string;
  } | null>(null);

  // Fetch sent emails
  const { data: sentEmails = [], isLoading: loadingSent } = useQuery({
    queryKey: ["lead-sent-emails", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_sent_emails")
        .select("*")
        .eq("lead_id", leadId)
        .order("sent_at", { ascending: false });

      if (error) throw error;
      return data as SentEmail[];
    },
    enabled: !!leadId,
  });

  // Set up realtime subscription for sent emails
  useEffect(() => {
    if (!leadId) return;

    const channel = supabase
      .channel(`lead-sent-emails-${leadId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "lead_sent_emails",
          filter: `lead_id=eq.${leadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["lead-sent-emails", leadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [leadId, queryClient]);

  // Combine and sort emails chronologically
  const allEmails: CombinedEmail[] = [
    ...receivedEmails.map((e) => ({ ...e, direction: "inbound" as const })),
    ...sentEmails.map((e) => ({ ...e, direction: "outbound" as const })),
  ].sort((a, b) => {
    const dateA = a.direction === "inbound" ? new Date(a.received_at) : new Date(a.sent_at);
    const dateB = b.direction === "inbound" ? new Date(b.received_at) : new Date(b.sent_at);
    return dateB.getTime() - dateA.getTime();
  });

  const isLoading = loadingReceived || loadingSent;

  const toggleExpanded = (emailId: string) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
        // Mark inbound as read when expanded
        const email = allEmails.find((e) => e.id === emailId);
        if (email && email.direction === "inbound" && !email.is_read) {
          markAsRead(emailId);
        }
      }
      return newSet;
    });
  };

  const handleReply = (email: CombinedEmail) => {
    const replyToAddress = email.direction === "inbound" 
      ? email.from_address 
      : email.to_addresses[0];
    
    const originalSubject = email.subject || "";
    const subject = originalSubject.startsWith("Re:") 
      ? originalSubject 
      : `Re: ${originalSubject}`;
    
    const originalBody = email.body_text || "";
    
    setReplyTo({
      emailId: email.direction === "inbound" ? email.id : undefined,
      to: replyToAddress,
      subject,
      originalBody,
    });
    setComposeOpen(true);
    
    // Mark as replied if inbound
    if (email.direction === "inbound") {
      markAsReplied(email.id);
    }
  };

  const handleNewEmail = () => {
    setReplyTo(clientEmail ? { to: clientEmail, subject: "" } : null);
    setComposeOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with compose button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {allEmails.length} email{allEmails.length !== 1 ? "s" : ""}
          </span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        <Button size="sm" onClick={handleNewEmail} className="gap-2">
          <Plus className="h-4 w-4" />
          Compose
        </Button>
      </div>

      {allEmails.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Mail className="h-12 w-12 text-muted-foreground/40 mb-4" />
          <h3 className="font-medium text-foreground mb-2">No emails yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Send an email to the client or receive emails at this lead's unique address.
          </p>
          <Button onClick={handleNewEmail} className="gap-2">
            <Send className="h-4 w-4" />
            Send First Email
          </Button>
        </div>
      ) : (
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-3">
            {allEmails.map((email) => (
              <EmailItem
                key={email.id}
                email={email}
                isExpanded={expandedEmails.has(email.id)}
                onToggle={() => toggleExpanded(email.id)}
                onReply={() => handleReply(email)}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Compose Dialog */}
      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        leadId={leadId}
        defaultTo={replyTo?.to || clientEmail || ""}
        defaultSubject={replyTo?.subject || ""}
        replyToEmailId={replyTo?.emailId}
        originalBody={replyTo?.originalBody}
      />
    </div>
  );
};

interface EmailItemProps {
  email: CombinedEmail;
  isExpanded: boolean;
  onToggle: () => void;
  onReply: () => void;
  formatFileSize: (bytes: number) => string;
}

const EmailItem = ({ email, isExpanded, onToggle, onReply, formatFileSize }: EmailItemProps) => {
  const isInbound = email.direction === "inbound";
  
  // Fetch stored attachments for this email (only for inbound emails)
  const { 
    attachments: storedAttachments, 
    getDownloadUrl, 
    saveToDocuments, 
    isSaving 
  } = useEmailAttachments(isInbound ? email.id : "");

  const isRead = isInbound ? email.is_read : true;
  const date = isInbound ? email.received_at : email.sent_at;
  const subject = email.subject;
  const bodyText = email.body_text;
  const bodyHtml = email.body_html;
  
  // Legacy attachments metadata (for historical emails without stored files)
  const legacyAttachments = isInbound ? email.attachments || [] : [];
  
  // Determine which attachments to show: stored ones take priority
  const hasStoredAttachments = storedAttachments.length > 0;

  return (
    <Card 
      className={`transition-colors ${
        !isRead ? 'border-primary/50 bg-primary/5' : ''
      } ${!isInbound ? 'border-l-4 border-l-blue-500' : ''}`}
    >
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <div className="p-4 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-start gap-3">
              {/* Direction/Read indicator */}
              <div className="pt-1">
                {isInbound ? (
                  isRead ? (
                    <Circle className="h-2 w-2 text-muted-foreground/30" />
                  ) : (
                    <Circle className="h-2 w-2 fill-primary text-primary" />
                  )
                ) : (
                  <Send className="h-3 w-3 text-blue-500" />
                )}
              </div>

              {/* Email info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {isInbound ? (
                      <>
                        <span className={`font-medium truncate ${!isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {email.from_name || email.from_address}
                        </span>
                        {email.from_name && (
                          <span className="text-xs text-muted-foreground truncate hidden sm:inline">
                            &lt;{email.from_address}&gt;
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          Sent
                        </Badge>
                        <span className="text-muted-foreground truncate">
                          To: {email.to_addresses.join(", ")}
                        </span>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {format(new Date(date), "MMM d, h:mm a")}
                  </span>
                </div>

                <p className={`text-sm truncate ${!isRead ? 'font-medium' : ''}`}>
                  {subject || "(no subject)"}
                </p>

                {/* Preview of body when collapsed */}
                {!isExpanded && bodyText && (
                  <p className="text-xs text-muted-foreground truncate mt-1">
                    {bodyText.slice(0, 100)}...
                  </p>
                )}

                {/* Indicators */}
                <div className="flex items-center gap-2 mt-1">
                  {isInbound && email.attachments && email.attachments.length > 0 && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Paperclip className="h-3 w-3" />
                      {email.attachments.length}
                    </Badge>
                  )}
                  {isInbound && email.replied_at && (
                    <Badge variant="outline" className="text-xs gap-1 text-green-600">
                      <Check className="h-3 w-3" />
                      Replied
                    </Badge>
                  )}
                </div>
              </div>

              {/* Expand indicator */}
              <div className="pt-1">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t">
            {/* Full header details */}
            <div className="py-3 text-xs text-muted-foreground space-y-1">
              {isInbound ? (
                <>
                  <p><span className="font-medium">From:</span> {email.from_name ? `${email.from_name} <${email.from_address}>` : email.from_address}</p>
                  <p><span className="font-medium">To:</span> {email.to_addresses.join(", ")}</p>
                  {email.cc_addresses && email.cc_addresses.length > 0 && (
                    <p><span className="font-medium">CC:</span> {email.cc_addresses.join(", ")}</p>
                  )}
                  <p><span className="font-medium">Date:</span> {format(new Date(email.received_at), "EEEE, MMMM d, yyyy 'at' h:mm a")}</p>
                </>
              ) : (
                <>
                  <p><span className="font-medium">To:</span> {email.to_addresses.join(", ")}</p>
                  {email.cc_addresses && email.cc_addresses.length > 0 && (
                    <p><span className="font-medium">CC:</span> {email.cc_addresses.join(", ")}</p>
                  )}
                  <p><span className="font-medium">Sent:</span> {format(new Date(email.sent_at), "EEEE, MMMM d, yyyy 'at' h:mm a")}</p>
                </>
              )}
            </div>

            {/* Email body */}
            <div className="py-3 border-t">
              {bodyHtml ? (
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(bodyHtml, { USE_PROFILES: { html: true } }),
                  }}
                />
              ) : bodyText ? (
                <pre className="whitespace-pre-wrap text-sm font-sans">{bodyText}</pre>
              ) : (
                <p className="text-muted-foreground italic">No message content</p>
              )}
            </div>

            {/* Attachments (only for inbound) */}
            {isInbound && (hasStoredAttachments || legacyAttachments.length > 0) && (
              <div className="py-3 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Attachments ({hasStoredAttachments ? storedAttachments.length : legacyAttachments.length})
                </p>
                
                {/* Interactive stored attachments */}
                {hasStoredAttachments ? (
                  <div className="space-y-2">
                    {storedAttachments.map((att) => (
                      <EmailAttachmentItem
                        key={att.id}
                        attachment={att}
                        onGetDownloadUrl={getDownloadUrl}
                        onSaveToDocuments={saveToDocuments}
                        isSaving={isSaving}
                      />
                    ))}
                  </div>
                ) : (
                  // Fallback: Legacy metadata badges for historical emails
                  <div className="flex flex-wrap gap-2">
                    {legacyAttachments.map((att, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        <Paperclip className="h-3 w-3" />
                        {att.filename}
                        <span className="text-muted-foreground">({formatFileSize(att.size)})</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="pt-3 border-t flex gap-2">
              <Button size="sm" variant="outline" onClick={onReply} className="gap-2">
                <Reply className="h-4 w-4" />
                Reply
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
